// Reusable helpers for calendars + streaks

export const ymd = (d) => {
    const dt = d instanceof Date ? new Date(d) : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  
  export const parseYmd = (s) => {
    const [y, m, d] = String(s).split("-").map((n) => parseInt(n, 10));
    return new Date(y, (m || 1) - 1, d || 1);
  };
  
  /**
   * Build calendar marks and streaks from activities.
   * - activities: list of activity objects
   * - userId: only consider this user's activities (optional)
   * - activityDates: optional explicit list of date strings (YYYY-MM-DD)
   *
   * Returns:
   * {
   *   activityDates: [YYYY-MM-DD],
   *   starDates:     [YYYY-MM-DD] // next-day stamps
   *   streakCount:   number,
   *   starLinkByDate:{ [YYYY-MM-DD]: activityId }, // star day -> source activity id
   *   activityByDate:{ [YYYY-MM-DD]: activityId }, // activity day -> activity id (latest)
   * }
   */
  export const computeCalendarMarks = ({ activities = [], userId, activityDates }) => {
    const dateSet = new Set();
    const activityByDate = {}; // YYYY-MM-DD -> activityId (latest by timestamp)
  
    const add = (ds, a = null) => {
      if (!ds) return;
      dateSet.add(ds);
      if (a?.id) {
        // keep the latest activity for that day
        const prevId = activityByDate[ds];
        if (!prevId) {
          activityByDate[ds] = a.id;
        } else {
          const prev = activities.find((x) => x.id === prevId);
          const prevTs = new Date(prev?.timestamp || prev?.created_at || 0).getTime();
          const curTs = new Date(a.timestamp || a.created_at || 0).getTime();
          if (curTs > prevTs) activityByDate[ds] = a.id;
        }
      }
    };
  
    // Prefer explicit dates if provided
    if (Array.isArray(activityDates) && activityDates.length) {
      activityDates.forEach((ds) => add(ds));
    } else {
      activities.forEach((a) => {
        const uid =
          a?.user?.id ??
          a?.author?.id ??
          a?.user_id ??
          a?.author_id ??
          null;
        if (userId && String(uid) !== String(userId)) return;
        const ts = a?.timestamp || a?.created_at || a?.updated_at;
        if (!ts) return;
        add(ymd(new Date(ts)), a);
      });
    }
  
    // Star = next day after activity day
    const starSet = new Set();
    const starLinkByDate = {};
    dateSet.forEach((ds) => {
      const d = parseYmd(ds);
      d.setDate(d.getDate() + 1);
      const ns = ymd(d);
      starSet.add(ns);
      const srcId = activityByDate[ds];
      if (srcId) starLinkByDate[ns] = srcId;
    });
  
    // Simple "most recent contiguous" streak
    const sorted = Array.from(dateSet).sort();
    let streak = 0;
    if (sorted.length) {
      let cur = parseYmd(sorted[sorted.length - 1]); // last activity date
      while (dateSet.has(ymd(cur))) {
        streak += 1;
        cur.setDate(cur.getDate() - 1);
      }
    }
  
    return {
      activityDates: Array.from(dateSet),
      starDates: Array.from(starSet),
      streakCount: streak,
      starLinkByDate,
      activityByDate,
    };
  };
  
  export const streakCountFromByDay = (activitiesByDay = {}) =>
    computeCalendarMarks({
      activityDates: Object.keys(activitiesByDay || {}),
    }).streakCount;